/**
 * ReadingProgressBar — slim top-of-page bar that fills as the reader
 * scrolls the article body. Uses requestAnimationFrame for smooth
 * updates and cleans up its scroll listener on unmount.
 */
import { useEffect, useState } from "react";

export default function ReadingProgressBar({
  targetSelector = "article",
  color = "#0F766E",
  className = "",
  testId = "reading-progress",
}) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf = 0;
    const target = document.querySelector(targetSelector);
    const compute = () => {
      const viewH = window.innerHeight;
      if (target) {
        const rect = target.getBoundingClientRect();
        const total = rect.height - viewH;
        if (total > 0) {
          // Article overflows the viewport — measure against the target.
          const raw = Math.max(0, Math.min(1, -rect.top / total));
          setPct(Math.round(raw * 100));
          return;
        }
      }
      // Short article fits in one viewport OR target missing — fall
      // back to document scroll so the bar still fills as the reader
      // moves down the page.
      const docTotal = Math.max(1, document.documentElement.scrollHeight - viewH);
      const raw = Math.max(0, Math.min(1, window.scrollY / docTotal));
      setPct(Math.round(raw * 100));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    compute();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [targetSelector]);

  return (
    <div
      className={`fixed top-0 left-0 right-0 h-1 bg-transparent z-40 ${className}`}
      data-testid={testId}
      aria-hidden
    >
      <div
        className="h-full transition-[width] duration-100"
        style={{ width: `${pct}%`, background: color }}
        data-testid={`${testId}-fill`}
      />
    </div>
  );
}
